import React from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Badge } from "@/src/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";

import { formatClock } from "./liveFormat";

import type { JSX } from "react";

import type { LiveReplayRange } from "./live.types";
import type { LiveView } from "../../utils/liveView";

interface LiveReplayCardProps {
  view: LiveView;
  range: LiveReplayRange;
}

function LiveReplayCard({
  view,
  range,
}: LiveReplayCardProps): JSX.Element | null {
  const { t } = useTranslation("editor-content");
  const { state } = view;

  if (state === "ended") return null;

  function renderStatus(): JSX.Element {
    if (state === "replayCreated") {
      return <Badge variant="success">{t("liveRegie_replayCreated")}</Badge>;
    }
    if (state === "replayUnavailable") {
      return (
        <Badge variant="neutral">{t("liveRegie_replayUnavailableBadge")}</Badge>
      );
    }
    if (state === "replayAvailable") {
      return <Badge variant="neutral">{t("liveRegie_replayNotCreated")}</Badge>;
    }
    return <Badge variant="neutral">{t("liveRegie_replayPending")}</Badge>;
  }

  function renderBody(): JSX.Element {
    if (state === "stopping") {
      return <p>{t("liveRegie_replayAfterFinalization")}</p>;
    }
    if (state === "replayUnavailable") return <p>{t("replayUnavailable")}</p>;
    if (state === "replayAvailable")
      return <p>{t("liveRegie_replayExplain")}</p>;

    return (
      <dl className="flex flex-col gap-1.5 font-mono">
        <div className="flex justify-between">
          <dt className="text-muted-foreground font-sans">
            {t("liveRegie_replayStart")}
          </dt>
          <dd>{formatClock(range.start)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground font-sans">
            {t("liveRegie_replayEnd")}
          </dt>
          <dd>{formatClock(range.end)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground font-sans">
            {t("liveRegie_duration")}
          </dt>
          <dd>{formatClock(range.end - range.start)}</dd>
        </div>
        {view.replayUuid ? (
          <Link
            className="text-primary pt-1 font-sans underline"
            to={`/contents/hosted/${view.replayUuid}`}
          >
            {t("liveRegie_openReplayLink")}
          </Link>
        ) : null}
      </dl>
    );
  }

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="flex flex-row items-center justify-between gap-2 px-4">
        <CardTitle>{t("liveRegie_replayTitle")}</CardTitle>
        {renderStatus()}
      </CardHeader>
      <CardContent className="text-muted-foreground px-4 text-sm">
        {renderBody()}
      </CardContent>
    </Card>
  );
}

export default LiveReplayCard;
